import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { apiService, Asset } from '@/apiService';
import {
  Search,
  RefreshCw,
  Plus,
  Minus,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Database,
  Wifi,
  WifiOff,
  AlertTriangle,
  CheckCircle2,
  Download,
  Settings2,
  Filter,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  X,
  Bitcoin,
  TrendingUp,
  Zap,
} from 'lucide-react';

type SortField = 'symbol' | 'name' | 'exchange';
type SortDir = 'asc' | 'desc';
type AssetClassFilter = 'all' | 'equity' | 'crypto';
type StatusFilter = 'all' | 'subscribed' | 'idle';

interface SubscribedSymbolInfo {
  symbol: string;
  exchange: string;
  barBuilders: { period: string; timeUnits: number }[];
  availableCharts: { chartId: string; barType: string }[];
}

interface AssetManagerProps {
  className?: string;
  onSymbolSelect?: (symbol: string) => void;
}

const PAGE_SIZE = 50;

/* Custom dropdown to replace native <select> which ignores dark mode CSS on <option> elements */
const ExchangeDropdown: React.FC<{
  value: string;
  onChange: (val: string) => void;
  exchanges: string[];
}> = ({ value, onChange, exchanges }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const label = value === 'all' ? 'All Exchanges' : value;

  return (
    <div ref={ref} className="relative w-full">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-2 py-1.5 rounded border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-ring/50"
      >
        <span className="truncate">{label}</span>
        <ChevronDown className={`h-3.5 w-3.5 ml-1 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full max-h-60 overflow-y-auto rounded border border-input bg-background shadow-lg">
          <div
            onClick={() => { onChange('all'); setOpen(false); }}
            className={`px-2 py-1.5 text-sm cursor-pointer hover:bg-accent hover:text-accent-foreground ${value === 'all' ? 'bg-accent/50 text-accent-foreground' : 'text-foreground'}`}
          >
            All Exchanges
          </div>
          {exchanges.map(ex => (
            <div
              key={ex}
              onClick={() => { onChange(ex); setOpen(false); }}
              className={`px-2 py-1.5 text-sm cursor-pointer hover:bg-accent hover:text-accent-foreground ${value === ex ? 'bg-accent/50 text-accent-foreground' : 'text-foreground'}`}
            >
              {ex}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const AssetManager: React.FC<AssetManagerProps> = ({ className, onSymbolSelect }) => {
  const [allAssets, setAllAssets] = useState<Asset[]>([]);
  const [subscribedSymbols, setSubscribedSymbols] = useState<SubscribedSymbolInfo[]>([]);
  const [allSubscribedNames, setAllSubscribedNames] = useState<string[]>([]);
  const [subTotalCount, setSubTotalCount] = useState(0);
  const [subTotalPages, setSubTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('symbol');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [activeView, setActiveView] = useState<'subscribed' | 'browse'>('subscribed');
  const [error, setError] = useState<string | null>(null);
  const [websocketLimit, setWebsocketLimit] = useState<number>(1000);
  const [dataFeed, setDataFeed] = useState<string>('');

  // Bulk subscribe top assets
  const [bulkCount, setBulkCount] = useState<number>(500);
  const [bulkSortBy, setBulkSortBy] = useState<string>('exchange');
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResult, setBulkResult] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [totalSubscribedViaBulk, setTotalSubscribedViaBulk] = useState<number>(0);

  // Filters
  const [assetClassFilter, setAssetClassFilter] = useState<AssetClassFilter>('all');
  const [exchangeFilter, setExchangeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [showShortable, setShowShortable] = useState(false);
  const [showFractionable, setShowFractionable] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);
  const [subPage, setSubPage] = useState(1);
  const SUB_PAGE_SIZE = 100;

  // Load subscribed symbols and stored assets on mount
  useEffect(() => {
    loadSubscriptions(subPage);
    loadSubscribedNames();
    loadStoredAssets();
    loadSubscriptionStats();
  }, []);

  // Refetch subscribed symbols when page changes
  useEffect(() => {
    loadSubscriptions(subPage);
  }, [subPage]);

  const loadSubscriptionStats = async () => {
    try {
      const stats = await apiService.getSubscriptionStats();
      if (stats?.maxEquitySubscriptions) {
        setWebsocketLimit(stats.maxEquitySubscriptions);
      }
      if (stats?.dataFeed) {
        setDataFeed(stats.dataFeed);
      }
    } catch (err) {
      console.error('Failed to load subscription stats:', err);
    }
  };

  const loadSubscribedNames = async () => {
    try {
      const resp = await apiService.getSubscribedSymbolNames();
      setAllSubscribedNames(resp.symbols ?? []);
    } catch (err) {
      console.error('Failed to load subscribed symbol names:', err);
    }
  };

  const loadSubscriptions = async (pg?: number) => {
    try {
      const response = await apiService.getSubscribedSymbols(pg ?? subPage, SUB_PAGE_SIZE);
      if (response?.subscribedSymbols) {
        setSubscribedSymbols(response.subscribedSymbols);
        setSubTotalCount(response.totalCount ?? response.subscribedSymbols.length);
        setSubTotalPages(response.totalPages ?? 1);
      }
    } catch (err) {
      console.error('Failed to load subscriptions:', err);
    }
  };

  const loadStoredAssets = async () => {
    setLoading(true);
    try {
      const assets = await apiService.getStoredAssets();
      setAllAssets(assets);
      setError(null);
    } catch {
      // If no stored assets, try fetching the asset list from the provider
      setError('No stored assets found. Click "Fetch Asset List" to download it.');
    } finally {
      setLoading(false);
    }
  };

  const fetchAssetList = async () => {
    setFetching(true);
    setError(null);
    try {
      await apiService.fetchAssets();
      // Now reload stored assets
      await loadStoredAssets();
    } catch (err: any) {
      setError(`Failed to fetch assets: ${err.message}`);
    } finally {
      setFetching(false);
    }
  };

  const handleSubscribe = useCallback(async (symbol: string) => {
    setActionLoading(symbol);
    try {
      await apiService.subscribeToSymbol(symbol, '1M');
      await loadSubscriptions();
      await loadSubscribedNames();
      setError(null);
    } catch (err: any) {
      if (err.message?.includes('429') || err.message?.includes('limit')) {
        setError(`WebSocket limit reached (${websocketLimit}). Unsubscribe from a symbol first.`);
      } else {
        setError(`Failed to subscribe to ${symbol}: ${err.message}`);
      }
    } finally {
      setActionLoading(null);
    }
  }, []);

  const handleBulkSubscribe = useCallback(async () => {
    setBulkLoading(true);
    setBulkResult(null);
    try {
      const result = await apiService.subscribeTopUsStocks(bulkCount, bulkSortBy);
      const newCount = result.succeeded || 0;
      setTotalSubscribedViaBulk(prev => prev + newCount);
      setBulkResult({
        message: newCount > 0
          ? `Subscribed to ${newCount} new symbols${result.failed ? ` (${result.failed} failed)` : ''}${result.limitReached ? ' — limit reached' : ''}`
          : 'No new symbols to subscribe (all already subscribed or limit reached)',
        type: newCount > 0 ? 'success' : 'error'
      });
      await loadSubscriptions();
      await loadSubscribedNames();
      await loadSubscriptionStats();
    } catch (err: any) {
      setBulkResult({ message: err.message || 'Failed to subscribe', type: 'error' });
    } finally {
      setBulkLoading(false);
    }
  }, [bulkCount, bulkSortBy]);

  const handleSubscribeAll = useCallback(async () => {
    setBulkLoading(true);
    setBulkResult(null);
    try {
      const result = await apiService.subscribeAllUsEquities(bulkSortBy);
      const newCount = result.succeeded || 0;
      setTotalSubscribedViaBulk(prev => prev + newCount);
      setBulkResult({
        message: newCount > 0
          ? `Subscribed to all US equities: ${newCount.toLocaleString()} new symbols in ${(result as any).batches || '?'} batches${result.failed ? ` (${result.failed} failed)` : ''}${result.limitReached ? ' — limit reached' : ''}`
          : 'No new symbols to subscribe (all already subscribed or limit reached)',
        type: newCount > 0 ? 'success' : 'error'
      });
      await loadSubscriptions();
      await loadSubscribedNames();
      await loadSubscriptionStats();
    } catch (err: any) {
      setBulkResult({ message: err.message || 'Failed to subscribe all', type: 'error' });
    } finally {
      setBulkLoading(false);
    }
  }, [bulkSortBy]);

  const handleUnsubscribe = useCallback(async (symbol: string) => {
    setActionLoading(symbol);
    try {
      await apiService.unsubscribeFromSymbol(symbol);
      await loadSubscriptions();
      await loadSubscribedNames();
      setError(null);
    } catch (err: any) {
      setError(`Failed to unsubscribe from ${symbol}: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  }, []);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const subscribedSet = useMemo(
    () => new Set(allSubscribedNames),
    [allSubscribedNames]
  );

  const slotsUsed = subTotalCount;
  const slotsRemaining = websocketLimit - slotsUsed;

  // Extract unique exchanges for filter dropdown
  const exchanges = useMemo(() => {
    const set = new Set(allAssets.map(a => a.exchange).filter(Boolean));
    return Array.from(set).sort();
  }, [allAssets]);

  // Asset class counts
  const classCounts = useMemo(() => {
    const equity = allAssets.filter(a => (a as any).class === 'UsEquity' || (a as any).class === 'us_equity').length;
    const crypto = allAssets.filter(a => (a as any).class === 'Crypto' || (a as any).class === 'crypto').length;
    return { equity, crypto, total: allAssets.length };
  }, [allAssets]);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [searchTerm, assetClassFilter, exchangeFilter, statusFilter, showShortable, showFractionable, sortField, sortDir]);

  // Filtered and sorted asset list for browse view
  const filteredAssets = useMemo(() => {
    let filtered = allAssets;

    // Search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(asset =>
        asset.symbol.toLowerCase().includes(term) ||
        (asset.name && asset.name.toLowerCase().includes(term))
      );
    }

    // Asset class filter
    if (assetClassFilter === 'equity') {
      filtered = filtered.filter(a => (a as any).class === 'UsEquity' || (a as any).class === 'us_equity');
    } else if (assetClassFilter === 'crypto') {
      filtered = filtered.filter(a => (a as any).class === 'Crypto' || (a as any).class === 'crypto');
    }

    // Exchange filter
    if (exchangeFilter !== 'all') {
      filtered = filtered.filter(a => a.exchange === exchangeFilter);
    }

    // Status filter
    if (statusFilter === 'subscribed') {
      filtered = filtered.filter(a => subscribedSet.has(a.symbol));
    } else if (statusFilter === 'idle') {
      filtered = filtered.filter(a => !subscribedSet.has(a.symbol));
    }

    // Boolean filters
    if (showShortable) {
      filtered = filtered.filter(a => a.shortable);
    }
    if (showFractionable) {
      filtered = filtered.filter(a => a.fractionable);
    }

    // Sort
    return filtered.sort((a, b) => {
      const aVal = a[sortField] ?? '';
      const bVal = b[sortField] ?? '';
      if (typeof aVal === 'string') {
        return sortDir === 'asc'
          ? aVal.localeCompare(bVal as string)
          : (bVal as string).localeCompare(aVal);
      }
      return 0;
    });
  }, [allAssets, searchTerm, sortField, sortDir, assetClassFilter, exchangeFilter, statusFilter, showShortable, showFractionable, subscribedSet]);

  // Paginated results
  const totalPages = Math.ceil(filteredAssets.length / PAGE_SIZE);
  const paginatedAssets = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredAssets.slice(start, start + PAGE_SIZE);
  }, [filteredAssets, page]);

  // Client-side search filter within the server-provided page
  const filteredSubscribed = useMemo(() => {
    if (!searchTerm) return subscribedSymbols;
    return subscribedSymbols.filter(s =>
      s.symbol.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [subscribedSymbols, searchTerm]);

  const SortIcon: React.FC<{ field: SortField }> = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 opacity-30" />;
    return sortDir === 'asc' ? <ArrowUp className="h-3 w-3 text-primary" /> : <ArrowDown className="h-3 w-3 text-primary" />;
  };

  return (
    <div className={className}>
      {/* Slot Usage Header */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <Card className="glass">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Active Subscriptions</p>
                <p className="text-2xl font-bold tabular-nums">{slotsUsed}</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Wifi className="h-5 w-5 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="glass">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Available Slots</p>
                <p className={`text-2xl font-bold tabular-nums ${slotsRemaining <= 3 ? 'text-amber-500' : slotsRemaining === 0 ? 'text-red-400' : 'text-emerald-500'}`}>
                  {slotsRemaining}
                </p>
              </div>
              <div className={`h-10 w-10 rounded-full flex items-center justify-center ${slotsRemaining <= 3 ? 'bg-amber-500/10' : 'bg-emerald-500/10'}`}>
                {slotsRemaining === 0
                  ? <WifiOff className="h-5 w-5 text-red-400" />
                  : <CheckCircle2 className={`h-5 w-5 ${slotsRemaining <= 3 ? 'text-amber-500' : 'text-emerald-500'}`} />
                }
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="glass">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Asset Database</p>
                <p className="text-2xl font-bold tabular-nums">{allAssets.length.toLocaleString()}</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Database className="h-5 w-5 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Slot Progress Bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
          <span>WebSocket Slots{dataFeed ? ` (${dataFeed})` : ''}</span>
          <span>{slotsUsed} / {websocketLimit}</span>
        </div>
        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-300 ${
              slotsUsed >= websocketLimit ? 'bg-red-500' : slotsUsed >= websocketLimit * 0.8 ? 'bg-amber-500' : 'bg-emerald-500'
            }`}
            style={{ width: `${(slotsUsed / websocketLimit) * 100}%` }}
          />
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="mb-4 p-3 rounded-lg border border-amber-500/30 bg-amber-500/5 flex items-center gap-2 text-sm">
          <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
          <span className="text-amber-200">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-muted-foreground hover:text-foreground">
            ×
          </button>
        </div>
      )}

      <Card className="glass">
        <CardHeader className="pb-3">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Settings2 className="h-4.5 w-4.5 text-primary" />
              Asset Manager
              <Badge variant="outline" className="text-xs font-normal">
                {activeView === 'subscribed'
                  ? `${subTotalCount.toLocaleString()} active`
                  : `${filteredAssets.length.toLocaleString()} assets`}
              </Badge>
            </CardTitle>
            <div className="flex items-center gap-2">
              <div className="relative flex-1 md:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder={activeView === 'subscribed' ? 'Filter subscriptions...' : 'Search symbols or names...'}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-8 py-2 border border-input bg-background/50 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ring/50 transition-shadow"
                />
                {searchTerm && (
                  <button onClick={() => setSearchTerm('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              {activeView === 'browse' && (
                <Button
                  onClick={() => setShowFilters(!showFilters)}
                  variant={showFilters ? 'default' : 'outline'}
                  size="sm"
                  className="h-9"
                >
                  <Filter className="h-3.5 w-3.5 mr-1" />
                  Filters
                  {(assetClassFilter !== 'all' || exchangeFilter !== 'all' || statusFilter !== 'all' || showShortable || showFractionable) && (
                    <span className="ml-1 h-4 w-4 rounded-full bg-primary-foreground/20 text-[10px] flex items-center justify-center">
                      {[assetClassFilter !== 'all', exchangeFilter !== 'all', statusFilter !== 'all', showShortable, showFractionable].filter(Boolean).length}
                    </span>
                  )}
                </Button>
              )}
              <Button
                onClick={fetchAssetList}
                disabled={fetching}
                variant="outline"
                size="sm"
                className="h-9"
                title="Download the asset list and store it in the database"
              >
                <Download className={`h-3.5 w-3.5 mr-1 ${fetching ? 'animate-spin' : ''}`} />
                {fetching ? 'Fetching...' : 'Fetch'}
              </Button>
              <Button
                onClick={() => { loadSubscriptions(); loadStoredAssets(); }}
                disabled={loading}
                variant="outline"
                size="sm"
                className="h-9"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-3 pt-0">
          {/* View Toggle */}
          <div className="flex gap-1.5">
            <button
              onClick={() => setActiveView('subscribed')}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                activeView === 'subscribed'
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'bg-muted/50 text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              <Wifi className="h-3 w-3" />
              Active Subscriptions ({subscribedSymbols.length})
            </button>
            <button
              onClick={() => setActiveView('browse')}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                activeView === 'browse'
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'bg-muted/50 text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              <Database className="h-3 w-3" />
              Browse Assets ({allAssets.length.toLocaleString()})
            </button>
          </div>

          {/* Subscribe Top Assets Panel */}
          {activeView === 'subscribed' && (
            <div className="p-3 rounded-lg border bg-muted/20 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">Subscribe to Top US Equities</span>
                {totalSubscribedViaBulk > 0 && (
                  <span className="text-[11px] text-muted-foreground">
                    {totalSubscribedViaBulk.toLocaleString()} added this session
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <select
                  value={bulkCount}
                  onChange={e => setBulkCount(Number(e.target.value))}
                  className="px-2 py-1.5 rounded border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-ring/50"
                >
                  {[100, 250, 500, 750, 1000, 1500, 2000, 2500, 3000].map(n => (
                    <option key={n} value={n}>{n.toLocaleString()} symbols</option>
                  ))}
                </select>
                <select
                  value={bulkSortBy}
                  onChange={e => setBulkSortBy(e.target.value)}
                  className="px-2 py-1.5 rounded border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-ring/50"
                >
                  <option value="exchange">By Exchange (NYSE/NASDAQ first)</option>
                  <option value="fractionable">By Popularity (fractionable first)</option>
                  <option value="marketcap">By Market Cap (largest first) ⏳</option>
                  <option value="margin">By Margin Requirement (stable first)</option>
                  <option value="symbol">By Ticker Length (large caps)</option>
                  <option value="alpha">Alphabetical</option>
                </select>
                <Button
                  onClick={handleBulkSubscribe}
                  disabled={bulkLoading}
                  variant="default"
                  size="sm"
                  className="h-8"
                >
                  {bulkLoading ? (
                    <><RefreshCw className="h-3.5 w-3.5 mr-1 animate-spin" />Subscribing...</>
                  ) : (
                    <><TrendingUp className="h-3.5 w-3.5 mr-1" />
                      {subTotalCount > 0 ? `Subscribe Next ${bulkCount.toLocaleString()}` : `Subscribe Top ${bulkCount.toLocaleString()}`}
                    </>
                  )}
                </Button>
                <Button
                  onClick={handleSubscribeAll}
                  disabled={bulkLoading}
                  variant="outline"
                  size="sm"
                  className="h-8 border-emerald-500/50 text-emerald-500 hover:bg-emerald-500/10"
                >
                  {bulkLoading ? (
                    <><RefreshCw className="h-3.5 w-3.5 mr-1 animate-spin" />Subscribing...</>
                  ) : (
                    <><Zap className="h-3.5 w-3.5 mr-1" />Subscribe All</>
                  )}
                </Button>
              </div>
              {bulkResult && (
                <p className={`text-xs ${bulkResult.type === 'success' ? 'text-emerald-500' : 'text-red-400'}`}>
                  {bulkResult.message}
                </p>
              )}
              <p className="text-[11px] text-muted-foreground">
                Already subscribed symbols are automatically skipped. "Subscribe All" subscribes every tradable US equity in batches.
                Active: {subTotalCount.toLocaleString()} / {websocketLimit.toLocaleString()}
              </p>
            </div>
          )}

          {/* Filter Panel (Browse view only) */}
          {activeView === 'browse' && showFilters && (
            <div className="p-3 rounded-lg border bg-muted/20 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">Filters</span>
                <button
                  onClick={() => {
                    setAssetClassFilter('all');
                    setExchangeFilter('all');
                    setStatusFilter('all');
                    setShowShortable(false);
                    setShowFractionable(false);
                  }}
                  className="text-[11px] text-muted-foreground hover:text-foreground transition-colors"
                >
                  Clear all
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {/* Asset Class */}
                <div>
                  <label className="text-[11px] text-muted-foreground mb-1 block">Asset Class</label>
                  <div className="flex gap-1">
                    {([
                      { id: 'all' as AssetClassFilter, label: 'All', count: classCounts.total },
                      { id: 'equity' as AssetClassFilter, label: 'Equity', count: classCounts.equity },
                      { id: 'crypto' as AssetClassFilter, label: 'Crypto', count: classCounts.crypto },
                    ]).map(opt => (
                      <button
                        key={opt.id}
                        onClick={() => setAssetClassFilter(opt.id)}
                        className={`flex-1 px-2 py-1.5 rounded text-[11px] font-medium transition-all ${
                          assetClassFilter === opt.id
                            ? 'bg-primary text-primary-foreground shadow-sm'
                            : 'bg-background/60 text-muted-foreground hover:text-foreground hover:bg-background'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Exchange */}
                <div>
                  <label className="text-[11px] text-muted-foreground mb-1 block">Exchange</label>
                  <ExchangeDropdown
                    value={exchangeFilter}
                    onChange={setExchangeFilter}
                    exchanges={exchanges}
                  />
                </div>

                {/* Subscription Status */}
                <div>
                  <label className="text-[11px] text-muted-foreground mb-1 block">Status</label>
                  <div className="flex gap-1">
                    {([
                      { id: 'all' as StatusFilter, label: 'All' },
                      { id: 'subscribed' as StatusFilter, label: 'Live' },
                      { id: 'idle' as StatusFilter, label: 'Idle' },
                    ]).map(opt => (
                      <button
                        key={opt.id}
                        onClick={() => setStatusFilter(opt.id)}
                        className={`flex-1 px-2 py-1.5 rounded text-[11px] font-medium transition-all ${
                          statusFilter === opt.id
                            ? 'bg-primary text-primary-foreground shadow-sm'
                            : 'bg-background/60 text-muted-foreground hover:text-foreground hover:bg-background'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Boolean Toggle: Shortable */}
                <div>
                  <label className="text-[11px] text-muted-foreground mb-1 block">Properties</label>
                  <div className="flex flex-col gap-1">
                    <button
                      onClick={() => setShowShortable(!showShortable)}
                      className={`px-2 py-1.5 rounded text-[11px] font-medium transition-all text-left ${
                        showShortable
                          ? 'bg-primary text-primary-foreground shadow-sm'
                          : 'bg-background/60 text-muted-foreground hover:text-foreground hover:bg-background'
                      }`}
                    >
                      Shortable only
                    </button>
                    <button
                      onClick={() => setShowFractionable(!showFractionable)}
                      className={`px-2 py-1.5 rounded text-[11px] font-medium transition-all text-left ${
                        showFractionable
                          ? 'bg-primary text-primary-foreground shadow-sm'
                          : 'bg-background/60 text-muted-foreground hover:text-foreground hover:bg-background'
                      }`}
                    >
                      Fractionable only
                    </button>
                  </div>
                </div>

                {/* Active Filter Tags */}
                <div>
                  <label className="text-[11px] text-muted-foreground mb-1 block">Active</label>
                  <div className="flex flex-wrap gap-1">
                    {assetClassFilter !== 'all' && (
                      <Badge variant="outline" className="text-[10px] gap-1 cursor-pointer hover:bg-destructive/10" onClick={() => setAssetClassFilter('all')}>
                        {assetClassFilter === 'equity' ? 'Equity' : 'Crypto'}
                        <X className="h-2.5 w-2.5" />
                      </Badge>
                    )}
                    {exchangeFilter !== 'all' && (
                      <Badge variant="outline" className="text-[10px] gap-1 cursor-pointer hover:bg-destructive/10" onClick={() => setExchangeFilter('all')}>
                        {exchangeFilter}
                        <X className="h-2.5 w-2.5" />
                      </Badge>
                    )}
                    {statusFilter !== 'all' && (
                      <Badge variant="outline" className="text-[10px] gap-1 cursor-pointer hover:bg-destructive/10" onClick={() => setStatusFilter('all')}>
                        {statusFilter === 'subscribed' ? 'Live' : 'Idle'}
                        <X className="h-2.5 w-2.5" />
                      </Badge>
                    )}
                    {showShortable && (
                      <Badge variant="outline" className="text-[10px] gap-1 cursor-pointer hover:bg-destructive/10" onClick={() => setShowShortable(false)}>
                        Shortable
                        <X className="h-2.5 w-2.5" />
                      </Badge>
                    )}
                    {showFractionable && (
                      <Badge variant="outline" className="text-[10px] gap-1 cursor-pointer hover:bg-destructive/10" onClick={() => setShowFractionable(false)}>
                        Fractionable
                        <X className="h-2.5 w-2.5" />
                      </Badge>
                    )}
                    {assetClassFilter === 'all' && exchangeFilter === 'all' && statusFilter === 'all' && !showShortable && !showFractionable && (
                      <span className="text-[11px] text-muted-foreground py-1">No filters active</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Subscribed Symbols View */}
          {activeView === 'subscribed' && (
            <div className="border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted/40 border-b">
                      <th className="px-3 py-2.5 text-xs font-medium text-muted-foreground text-left">Symbol</th>
                      <th className="px-3 py-2.5 text-xs font-medium text-muted-foreground text-left">Exchange</th>
                      <th className="px-3 py-2.5 text-xs font-medium text-muted-foreground text-left">Timeframes</th>
                      <th className="px-3 py-2.5 text-xs font-medium text-muted-foreground text-left">Charts</th>
                      <th className="px-3 py-2.5 text-xs font-medium text-muted-foreground text-right w-32">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/50">
                    {filteredSubscribed.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="py-12 text-center text-muted-foreground text-sm">
                          {subTotalCount === 0 ? 'No active subscriptions' : 'No matches'}
                        </td>
                      </tr>
                    ) : (
                      filteredSubscribed.map(sub => (
                        <tr
                          key={sub.symbol}
                          className="hover:bg-accent/40 cursor-pointer transition-colors"
                          onClick={() => onSymbolSelect?.(sub.symbol)}
                        >
                          <td className="px-3 py-2.5">
                            <div className="flex items-center gap-2.5">
                              <div className="h-8 w-8 rounded-lg bg-emerald-500/10 flex items-center justify-center text-[10px] font-bold text-emerald-500">
                                {sub.symbol.slice(0, 2)}
                              </div>
                              <span className="font-semibold">{sub.symbol}</span>
                            </div>
                          </td>
                          <td className="px-3 py-2.5 text-muted-foreground">{sub.exchange || '—'}</td>
                          <td className="px-3 py-2.5">
                            <div className="flex flex-wrap gap-1">
                              {sub.barBuilders?.map((bb, i) => (
                                <Badge key={i} variant="outline" className="text-[10px]">
                                  {bb.timeUnits}{bb.period === 'Minute' ? 'M' : bb.period === 'Hourly' ? 'H' : bb.period === 'Daily' ? 'D' : bb.period}
                                </Badge>
                              ))}
                            </div>
                          </td>
                          <td className="px-3 py-2.5 text-muted-foreground tabular-nums">
                            {sub.availableCharts?.length || 0}
                          </td>
                          <td className="px-3 py-2.5 text-right">
                            <Button
                              onClick={(e) => { e.stopPropagation(); handleUnsubscribe(sub.symbol); }}
                              disabled={actionLoading === sub.symbol}
                              variant="ghost"
                              size="sm"
                              className="h-7 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            >
                              {actionLoading === sub.symbol ? (
                                <RefreshCw className="h-3 w-3 animate-spin" />
                              ) : (
                                <>
                                  <Minus className="h-3 w-3 mr-1" />
                                  Unsubscribe
                                </>
                              )}
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Subscribed Pagination */}
              {subTotalPages > 1 && (
                <div className="flex items-center justify-between px-3 py-2.5 border-t bg-muted/20">
                  <div className="text-xs text-muted-foreground">
                    Showing {((subPage - 1) * SUB_PAGE_SIZE) + 1}–{Math.min(subPage * SUB_PAGE_SIZE, subTotalCount)} of {subTotalCount.toLocaleString()} subscriptions
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      onClick={() => setSubPage(1)}
                      disabled={subPage === 1}
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs px-2"
                    >
                      First
                    </Button>
                    <Button
                      onClick={() => setSubPage(p => Math.max(1, p - 1))}
                      disabled={subPage === 1}
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                    >
                      <ChevronLeft className="h-3.5 w-3.5" />
                    </Button>
                    <span className="text-xs tabular-nums px-2">
                      Page {subPage} of {subTotalPages}
                    </span>
                    <Button
                      onClick={() => setSubPage(p => Math.min(subTotalPages, p + 1))}
                      disabled={subPage >= subTotalPages}
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                    >
                      <ChevronRight className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      onClick={() => setSubPage(subTotalPages)}
                      disabled={subPage >= subTotalPages}
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs px-2"
                    >
                      Last
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Browse Assets View */}
          {activeView === 'browse' && (
            <div className="border rounded-lg overflow-hidden">
              {allAssets.length === 0 ? (
                <div className="py-16 text-center text-muted-foreground">
                  <Database className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p className="text-sm mb-2">No assets in database</p>
                  <p className="text-xs mb-4">Download the asset list to browse and subscribe</p>
                  <Button onClick={fetchAssetList} disabled={fetching} variant="outline" size="sm">
                    <Download className={`h-3.5 w-3.5 mr-1 ${fetching ? 'animate-spin' : ''}`} />
                    {fetching ? 'Fetching...' : 'Fetch Asset List'}
                  </Button>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto max-h-[550px] overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 z-10">
                        <tr className="bg-muted/80 border-b backdrop-blur-sm">
                          {([
                            { field: 'symbol' as SortField, label: 'Symbol', align: 'left' },
                            { field: 'name' as SortField, label: 'Name', align: 'left' },
                            { field: 'exchange' as SortField, label: 'Exchange', align: 'left' },
                          ] as const).map(col => (
                            <th
                              key={col.field}
                              onClick={() => handleSort(col.field)}
                              className={`px-3 py-2.5 text-xs font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors select-none ${col.align === 'right' ? 'text-right' : 'text-left'}`}
                            >
                              <span className="inline-flex items-center gap-1">
                                {col.label}
                                <SortIcon field={col.field} />
                              </span>
                            </th>
                          ))}
                          <th className="px-3 py-2.5 text-xs font-medium text-muted-foreground text-left">Class</th>
                          <th className="px-3 py-2.5 text-xs font-medium text-muted-foreground text-left">Properties</th>
                          <th className="px-3 py-2.5 text-xs font-medium text-muted-foreground text-left">Status</th>
                          <th className="px-3 py-2.5 text-xs font-medium text-muted-foreground text-right w-32">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border/50">
                        {paginatedAssets.map(asset => {
                          const isSubscribed = subscribedSet.has(asset.symbol);
                          const isCrypto = (asset as any).class === 'Crypto' || (asset as any).class === 'crypto';
                          return (
                            <tr
                              key={asset.symbol}
                              className={`transition-colors cursor-pointer ${
                                isSubscribed ? 'bg-emerald-500/5' : 'hover:bg-accent/40'
                              }`}
                              onClick={() => onSymbolSelect?.(asset.symbol)}
                            >
                              <td className="px-3 py-2">
                                <div className="flex items-center gap-2">
                                  <div className={`h-7 w-7 rounded-md flex items-center justify-center text-[9px] font-bold ${
                                    isSubscribed ? 'bg-emerald-500/10 text-emerald-500'
                                    : isCrypto ? 'bg-amber-500/10 text-amber-500'
                                    : 'bg-muted text-muted-foreground'
                                  }`}>
                                    {isCrypto ? <Bitcoin className="h-3.5 w-3.5" /> : asset.symbol.slice(0, 2)}
                                  </div>
                                  <span className="font-semibold text-sm">{asset.symbol}</span>
                                </div>
                              </td>
                              <td className="px-3 py-2 text-muted-foreground text-xs truncate max-w-[200px]">
                                {asset.name || '—'}
                              </td>
                              <td className="px-3 py-2 text-muted-foreground text-xs">{asset.exchange || '—'}</td>
                              <td className="px-3 py-2">
                                <Badge variant="outline" className={`text-[10px] ${isCrypto ? 'border-amber-500/30 text-amber-500' : 'text-muted-foreground'}`}>
                                  {isCrypto ? 'Crypto' : 'Equity'}
                                </Badge>
                              </td>
                              <td className="px-3 py-2">
                                <div className="flex gap-0.5">
                                  {asset.shortable && (
                                    <Badge variant="outline" className="text-[9px] px-1 py-0 text-muted-foreground">S</Badge>
                                  )}
                                  {asset.fractionable && (
                                    <Badge variant="outline" className="text-[9px] px-1 py-0 text-muted-foreground">F</Badge>
                                  )}
                                  {asset.marginable && (
                                    <Badge variant="outline" className="text-[9px] px-1 py-0 text-muted-foreground">M</Badge>
                                  )}
                                </div>
                              </td>
                              <td className="px-3 py-2">
                                {isSubscribed ? (
                                  <Badge variant="outline" className="text-[10px] bg-emerald-500/10 text-emerald-500 border-emerald-500/20">
                                    <Wifi className="h-2.5 w-2.5 mr-1" />
                                    Live
                                  </Badge>
                                ) : (
                                  <Badge variant="outline" className="text-[10px] text-muted-foreground">
                                    Idle
                                  </Badge>
                                )}
                              </td>
                              <td className="px-3 py-2 text-right">
                                {isSubscribed ? (
                                  <Button
                                    onClick={(e) => { e.stopPropagation(); handleUnsubscribe(asset.symbol); }}
                                    disabled={actionLoading === asset.symbol}
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                  >
                                    {actionLoading === asset.symbol ? (
                                      <RefreshCw className="h-3 w-3 animate-spin" />
                                    ) : (
                                      <>
                                        <Minus className="h-3 w-3 mr-1" />
                                        Remove
                                      </>
                                    )}
                                  </Button>
                                ) : (
                                  <Button
                                    onClick={(e) => { e.stopPropagation(); handleSubscribe(asset.symbol); }}
                                    disabled={actionLoading === asset.symbol || slotsRemaining === 0}
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 text-xs"
                                  >
                                    {actionLoading === asset.symbol ? (
                                      <RefreshCw className="h-3 w-3 animate-spin" />
                                    ) : (
                                      <>
                                        <Plus className="h-3 w-3 mr-1" />
                                        Subscribe
                                      </>
                                    )}
                                  </Button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="flex items-center justify-between px-3 py-2.5 border-t bg-muted/20">
                    <div className="text-xs text-muted-foreground">
                      Showing {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, filteredAssets.length)} of {filteredAssets.length.toLocaleString()} results
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        onClick={() => setPage(1)}
                        disabled={page === 1}
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs px-2"
                      >
                        First
                      </Button>
                      <Button
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                      >
                        <ChevronLeft className="h-3.5 w-3.5" />
                      </Button>
                      <span className="text-xs tabular-nums px-2">
                        Page {page} of {totalPages}
                      </span>
                      <Button
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page >= totalPages}
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                      >
                        <ChevronRight className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        onClick={() => setPage(totalPages)}
                        disabled={page >= totalPages}
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs px-2"
                      >
                        Last
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AssetManager;
